import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { netlifyFunctionUrl, publicUrl } from '@/lib/siteUrl';
import { UTA_CONFERENCE_SCHEDULE_TEXT } from '@/lib/eventTimes';
import SEO from '../components/ui/SEO';

export default function Contact() {
    const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('submitting');

        try {
            const form = e.target;
            const formData = new FormData(form);
            const data = Object.fromEntries(formData.entries());

            // 1. Soumission Netlify Forms (production uniquement — inexistant sur le seul serveur Vite)
            if (import.meta.env.PROD) {
                await fetch('/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams(formData).toString(),
                });
            }

            // 2. Secure Save to Supabase via Netlify Function (Backend Verification)
            if (window.grecaptcha) {
                const token = await new Promise(resolve => {
                    window.grecaptcha.enterprise.ready(async () => {
                        try {
                            const t = await window.grecaptcha.enterprise.execute('6LdFQG0sAAAAADWoCDLZIpSLjyWNivwHGDyCFdXz', { action: 'submit_contact' });
                            resolve(t);
                        } catch (e) {
                            console.error(e);
                            resolve(null);
                        }
                    });
                });

                if (token) {
                    const response = await fetch(netlifyFunctionUrl('submit-contact'), {
                        method: 'POST',
                        body: JSON.stringify({
                            name: `${data.prenom} ${data.nom}`,
                            email: data.email,
                            phone: data.tel,
                            message: data.message,
                            token: token
                        }),
                    });

                    if (!response.ok) {
                        const errData = await response.json();
                        console.error('Backend save failed:', errData);
                    }
                }
            }

            setFormStatus('success');
            form.reset();
        } catch (error) {
            console.error('Submission error:', error);
            setFormStatus('error');
        }
    };

    return (
        <>
            <SEO
                title="Contact | Université de Tous Âges Nord-Luxembourg (Barvaux-sur-Ourthe)"
                description="Contactez-nous pour toute question sur nos conférences, excursions ou adhésions à l'Université de Tous Âges Nord-Luxembourg (Barvaux-sur-Ourthe)."
            />

            <div className="pt-36 min-h-screen bg-gray-50 pb-12">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary-900 mb-8 text-center">Contactez-nous</h1>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                            {/* Contact Info & Map */}
                            <div className="space-y-8">
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-xl font-bold text-primary-900 mb-4">Informations pratiques</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3 text-gray-600">
                                            <MapPin className="text-accent-500 mt-1" size={20} />
                                            <div>
                                                <p className="font-medium text-primary-900">Salle du Royal Syndicat d'Initiative de Barvaux</p>
                                                <p>Parc Julienas 1</p>
                                                <p>6940 Barvaux-sur-Ourthe</p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-3 text-gray-600">
                                            <Mail className="text-accent-500 mt-1" size={20} />
                                            <div>
                                                <p className="font-medium text-primary-900">Email</p>
                                                <a href="mailto:daniellemottet47@gmail.com" className="hover:text-accent-500 transition-colors">
                                                    daniellemottet47@gmail.com
                                                </a>
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t border-gray-100 text-sm text-gray-600 leading-relaxed">
                                            <p className="font-medium text-primary-900 mb-1">Horaires des conférences</p>
                                            <p>{UTA_CONFERENCE_SCHEDULE_TEXT}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-xl overflow-hidden shadow-sm h-64 md:h-80 relative">
                                    <iframe
                                        src="https://maps.google.com/maps?q=Royal+Syndicat+d'Initiative+de+Barvaux+Parc+Julienas+1&t=&z=15&ie=UTF8&iwloc=&output=embed"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen=""
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title="Carte Salle Royal Syndicat d'Initiative"
                                        className="absolute inset-0 w-full h-full"
                                    ></iframe>
                                </div>

                                <div className="rounded-xl overflow-hidden shadow-sm">
                                    <img
                                        src={publicUrl('/images/uta-room.avif')}
                                        alt="Salle de conférence UTA"
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                            </div>

                            {/* Contact Form */}
                            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-accent-500">
                                <h2 className="text-2xl font-bold text-primary-900 mb-6">Envoyez-nous un message</h2>

                                <form
                                    name="contact"
                                    method="POST"
                                    data-netlify="true"
                                    onSubmit={handleSubmit}
                                    className="space-y-5"
                                >
                                    <input type="hidden" name="form-name" value="contact" />

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                                            <input type="text" id="nom" name="nom" required className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none transition-all" />
                                        </div>
                                        <div>
                                            <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                                            <input type="text" id="prenom" name="prenom" required className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none transition-all" />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                        <input type="email" id="email" name="email" required className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none transition-all" />
                                    </div>

                                    <div>
                                        <label htmlFor="tel" className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                                        <input type="tel" id="tel" name="tel" className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none transition-all" />
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message (max 1500 mots)</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows={6}
                                            required
                                            maxLength={10000} // approx for 1500 words
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none transition-all resize-none"
                                        ></textarea>
                                        <p className="text-xs text-gray-400 mt-1 text-right">Max. ~1500 mots</p>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={formStatus === 'submitting' || formStatus === 'success'}
                                        className={`w-full py-3 px-6 rounded-lg font-bold text-white transition-all transform hover:-translate-y-1 ${formStatus === 'success'
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : 'bg-primary-600 hover:bg-primary-700'
                                            }`}
                                    >
                                        {formStatus === 'submitting' ? 'Envoi en cours...' :
                                            formStatus === 'success' ? 'Message envoyé !' :
                                                <span className="flex items-center justify-center gap-2">Envoyer <Send size={18} /></span>}
                                    </button>

                                    {formStatus === 'success' && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="p-4 bg-green-50 text-green-800 rounded-lg text-sm text-center"
                                        >
                                            Merci ! Votre message a bien été envoyé. Nous vous répondrons dans les plus brefs délais.
                                        </motion.div>
                                    )}

                                    {formStatus === 'error' && (
                                        <div className="p-4 bg-red-50 text-red-800 rounded-lg text-sm text-center">
                                            Une erreur est survenue. Merci de réessayer plus tard.
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </>
    );
}
